import type { Icon } from "@phosphor-icons/react";
import PhaseSixIcon, { type TIconNames } from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import { ClassActivityCategory, selectedCardStyles } from "../../../constants/ClassActivityConstants";

interface ClassSelectionCardProps {
    "data-testid"?: string;
    title: string;
    subtitle?: string;
    isSelected: boolean;
    isDashed: boolean;
    isDisabled: boolean;
    classActivityCategory: ClassActivityCategory;
    onClick: () => void;

    icon?: TIconNames;
    phosphorIcon?: Icon;
}

const selectedIconStyles: Record<ClassActivityCategory, string> = {
    [ClassActivityCategory.TEST]: "bg-p6-orange text-white",
    [ClassActivityCategory.VOCAB_TRAINING]: "bg-forest-green-800 text-white",
};

const getCardStyles = (props: ClassSelectionCardProps) => {
    if (props.isSelected) return `${selectedCardStyles[props.classActivityCategory]} p-s`;
    if (props.isDashed) return "border-2 border-dashed border-p6-grey-dark p-s";
    return "border-2 border-transparent outline-1 outline-p6-grey-mid bg-white p-s";
};

const getIconCircleStyles = (props: ClassSelectionCardProps) => {
    if (props.isSelected) return `${selectedIconStyles[props.classActivityCategory]} border border-transparent`;
    if (props.isDashed) return "text-p6-grey-dark border border-transparent";
    return "border border-p6-grey-dark text-p6-grey-dark";
};

export const ClassSelectionCard = (props: ClassSelectionCardProps) => {
    const renderIcon = () => {
        if (props.phosphorIcon) {
            return (
                <PhosphorIcon
                    phosphorIcon={props.phosphorIcon}
                    fontSize="text-[30px]"
                />
            );
        }
        if (props.icon) {
            return (
                <PhaseSixIcon
                    name={props.icon}
                    className="text-[30px]"
                />
            );
        }
        return null;
    };

    return (
        <button
            type="button"
            data-testid={props["data-testid"]}
            onClick={props.onClick}
            disabled={props.isDisabled}
            aria-pressed={props.isSelected}
            className={`flex gap-m items-center rounded-[12px] text-left flex-1 ${getCardStyles(props)} ${
                props.isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
        >
            <div
                className={`shrink-0 p-xxs rounded-circle flex items-center justify-center ${getIconCircleStyles(
                    props
                )}`}
            >
                {renderIcon()}
            </div>
            <div className="flex flex-col">
                <span className="text-l font-semibold text-p6-grey-dark line-clamp-2">{props.title}</span>
                {props.subtitle && <span className="text-m text-p6-grey-dark">{props.subtitle}</span>}
            </div>
        </button>
    );
};
